import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Updated imports for tsparticles
import { loadSlim } from "tsparticles-slim"; // Using slim instead of full
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import Particles from 'react-tsparticles'; // Correct
import { loadFull } from 'tsparticles';      // Correct
import { login, resendVerification } from '../api/auth';

import {
    Box, Typography, TextField, Button, Alert, Paper, Stack,
    CircularProgress, InputAdornment, Fade, Link as MuiLink,
    Container, Avatar, IconButton, Tooltip, Divider
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import RecyclingIcon from '@mui/icons-material/Recycling';
import NatureIcon from '@mui/icons-material/Nature';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupsIcon from '@mui/icons-material/Groups';
import StarIcon from '@mui/icons-material/Star';
import LoginIcon from '@mui/icons-material/Login';
import SecurityIcon from '@mui/icons-material/Security';




export default function LoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [values, setValues] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [animate, setAnimate] = useState(false);
    const { isAuthenticated } = useAuth();


    const [showResend, setShowResend] = useState(false);
    const [resendStatus, setResendStatus] = useState({ loading: false, message: '', error: '' });



    // Handle resend verification email


    const handleResend = async () => {
        setResendStatus({ loading: true, message: '', error: '' });
        try {
            const response = await resendVerification(values.email);
            setResendStatus({ loading: false, message: response.data.message, error: '' });
            setShowResend(false); // Hide the button after success
            setError(''); // Clear the old error
        } catch (err) {
            setResendStatus({ loading: false, message: '', error: err?.response?.data?.error || 'Failed to resend email.' });
        }
    };

    // Enhanced particle configuration - simplified to avoid compatibility issues
    const particlesConfig = {
        particles: {
            number: {
                value: 40,
                density: {
                    enable: true,
                    value_area: 800
                }
            },
            color: {
                value: ["#43a047", "#ffc400", "#2196f3"]
            },
            shape: {
                type: "circle"
            },
            opacity: {
                value: 0.5,
                random: true
            },
            size: {
                value: 3,
                random: true
            },
            move: {
                enable: true,
                speed: 1,
                direction: "none",
                random: false,
                straight: false,
                outModes: "out"
            }
        },
        interactivity: {
            events: {
                onHover: {
                    enable: true,
                    mode: "grab"
                },
                onClick: {
                    enable: true,
                    mode: "push"
                }
            },
            modes: {
                grab: {
                    distance: 140,
                    links: {
                        opacity: 1
                    }
                },
                push: {
                    quantity: 4
                }
            }
        },
        background: {
            color: {
                value: "transparent"
            }
        }
    };

    useEffect(() => {
        // Trigger animation after a delay
        const timer = setTimeout(() => {
            setAnimate(true);
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    // Updated initialization for tsparticles-slim
    const particlesInit = async (engine) => {
        await loadSlim(engine);
    };

    const handleChange = (e) =>
        setValues({ ...values, [e.target.name]: e.target.value });


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setShowResend(false); // Reset on new submission
        setResendStatus({ loading: false, message: '', error: '' }); // Reset resend status

        try {
            await login(values);
            navigate('/');
        } catch (err) {
            const errorMessage = err?.response?.data?.error || 'Login failed. Please check your credentials.';
            setError(errorMessage);
            // If the specific error is for an unverified account, show the resend option
            if (errorMessage.includes("not verified")) {
                setShowResend(true);
            }
        } finally {
            setLoading(false);
        }
    };

    // Check if user is already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    // Floating animation for decorative elements
    const floatingIcons = [
        { icon: <RecyclingIcon />, color: '#2196f3', delay: 0 },
        { icon: <NatureIcon />, color: '#4caf50', delay: 0.5 },
        { icon: <EnergySavingsLeafIcon />, color: '#ffc400', delay: 1 },
        { icon: <AutoAwesomeIcon />, color: '#9c27b0', delay: 2 }

    ];




    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 128px)' }}>
            <Header isAuthenticated={isAuthenticated} />

            <Box sx={{ position: 'relative', overflow: 'hidden', flexGrow: 1 }}>
                {/* Updated Particles component */}
                <Particles
                    id="tsparticles"
                    init={particlesInit}
                    options={particlesConfig}
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        zIndex: 0
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Rest of your component remains the same */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 8,
                            minHeight: 'calc(100vh - 140px)', // Account for header/footer
                        }}
                    >
                        {/* Left side - Welcome message (visible on larger screens) */}
                        <Fade in={animate} timeout={1000} style={{ transitionDelay: '100ms' }}>
                            <Box
                                sx={{
                                    maxWidth: 450,
                                    mb: { xs: 5, md: 0 },
                                    display: { xs: 'none', md: 'block' },
                                    position: 'relative',
                                    p: 4,
                                    borderRadius: 4,
                                    backdropFilter: 'blur(8px)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)',
                                }}
                            >
                                {/* Animated highlight badge */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -15,
                                        left: 20,
                                        backgroundColor: '#ffc400',
                                        color: '#212121',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 4,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        boxShadow: '0 4px 12px rgba(255, 196, 0, 0.3)',
                                        animation: 'pulse 2s infinite',
                                        '@keyframes pulse': {
                                            '0%': { transform: 'scale(1)' },
                                            '50%': { transform: 'scale(1.05)' },
                                            '100%': { transform: 'scale(1)' }
                                        }
                                    }}
                                >
                                    <EnergySavingsLeafIcon sx={{ fontSize: 16 }} /> Therap JavaFest - Short-listed App
                                </Box>

                                {/* Main Title with Animated Gradient */}
                                <Typography
                                    variant="h3"
                                    fontWeight={700}
                                    gutterBottom
                                    sx={{
                                        position: 'relative',
                                        backgroundImage: 'linear-gradient(90deg, #1b5e20, #43a047, #66bb6a)',
                                        backgroundSize: '200% auto',
                                        backgroundClip: 'text',
                                        textFillColor: 'transparent',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        animation: 'gradient 3s ease infinite',
                                        '@keyframes gradient': {
                                            '0%': { backgroundPosition: '0% center' },
                                            '50%': { backgroundPosition: '100% center' },
                                            '100%': { backgroundPosition: '0% center' }
                                        }
                                    }}
                                >
                                    Welcome to Sortify
                                </Typography>

                                {/* Subtitle with Icon */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <RecyclingIcon
                                        color="primary"
                                        sx={{
                                            mr: 1,
                                            animation: 'rotate 3s linear infinite',
                                            '@keyframes rotate': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' }
                                            }
                                        }}
                                    />
                                    <Typography
                                        variant="h5"
                                        color="primary.dark"
                                        fontWeight={600}
                                        sx={{
                                            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        Your Smart Waste Buddy
                                    </Typography>
                                </Box>

                                {/* Engaging description with bullet points */}
                                <Typography variant="body1" paragraph sx={{ mb: 3, color: 'text.secondary', fontWeight: 500 }}>
                                    Join our community of environmental champions and make a difference with every scan!
                                </Typography>

                                {/* Feature highlights with icons */}
                                <Box sx={{ mb: 3 }}>
                                    {[
                                        { icon: <AutoAwesomeIcon />, text: "AI-powered waste classification" },
                                        { icon: <BarChartIcon />, text: "Track your environmental impact" },
                                        { icon: <GroupsIcon />, text: "Join our eco-conscious community" }
                                    ].map((item, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 1.5,
                                                transform: animate ? 'translateX(0)' : 'translateX(-20px)',
                                                opacity: animate ? 1 : 0,
                                                transition: `all 0.5s ease-out ${0.3 + (index * 0.1)}s`,
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: 'primary.light',
                                                    mr: 1.5,
                                                }}
                                            >
                                                {item.icon}
                                            </Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {item.text}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Social proof */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor: 'rgba(67, 160, 71, 0.1)',
                                        borderRadius: 2,
                                        p: 2,
                                        mt: 3
                                    }}
                                >
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            Join our growing community
                                        </Typography>
                                        <Typography variant="h6" fontWeight={700} color="primary.dark">
                                            10,000+ Users
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex' }}>
                                        {[...Array(5)].map((_, i) => (
                                            <StarIcon key={i} sx={{ color: '#ffc400', fontSize: 20 }} />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Floating icons remain unchanged */}
                                {floatingIcons.map((item, index) => (
                                    <Avatar
                                        key={index}
                                        sx={{
                                            position: 'absolute',
                                            backgroundColor: item.color,
                                            width: 50,
                                            height: 50,
                                            top: `${10 + (index * 30)}%`,
                                            right: `${-20 - (index * 15)}%`,
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                            animation: `float ${2 + (index * 0.5)}s ease-in-out infinite alternate`,
                                            '@keyframes float': {
                                                '0%': { transform: 'translateY(0px)' },
                                                '100%': { transform: 'translateY(-10px)' }
                                            },
                                            opacity: 0.8
                                        }}
                                    >
                                        {item.icon}
                                    </Avatar>
                                ))}
                            </Box>
                        </Fade>

                        {/* Rest of your code stays the same */}
                        <Fade in={animate} timeout={1200} style={{ transitionDelay: '300ms' }}>
                            <Paper
                                elevation={4}
                                sx={{
                                    p: { xs: 3, sm: 5 },
                                    maxWidth: 420,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '24px',
                                    boxShadow: '0 8px 40px rgba(67, 160, 71, 0.15)',
                                    transform: animate ? 'translateY(0)' : 'translateY(20px)',
                                    transition: 'transform 0.6s ease-out',
                                }}
                            >
                                {/* Content unchanged */}
                                <Avatar
                                    sx={{
                                        mb: 2,
                                        bgcolor: 'primary.main',
                                        width: 56,
                                        height: 56,
                                        boxShadow: '0 4px 12px rgba(67, 160, 71, 0.2)',
                                    }}
                                >
                                    <EnergySavingsLeafIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Typography component="h1" variant="h4" fontWeight={700} color="text.primary" gutterBottom>
                                    Welcome Back!
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center" mb={3}>
                                    Log in to continue your sorting journey.
                                </Typography>


                                {/* Show resend button only if the error indicates unverified account */}
                                {error && (
                                    <Alert
                                        severity="error"
                                        sx={{ width: '100%', mb: 2 }}
                                        action={
                                            showResend && (
                                                <Button
                                                    color="inherit"
                                                    size="small"
                                                    onClick={handleResend}
                                                    disabled={resendStatus.loading}
                                                >
                                                    {resendStatus.loading ? <CircularProgress size={20} color="inherit" /> : 'Resend Email'}
                                                </Button>
                                            )
                                        }
                                    >
                                        {error}
                                    </Alert>
                                )}
                                {resendStatus.message && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{resendStatus.message}</Alert>}
                                {resendStatus.error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{resendStatus.error}</Alert>}






                                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            fullWidth label="Email Address" name="email" type="email"
                                            autoComplete="email" value={values.email} onChange={handleChange} required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailOutlinedIcon sx={{ color: 'text.secondary' }} />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <TextField
                                            fullWidth label="Password" name="password" type={values.showPassword ? 'text' : 'password'}
                                            autoComplete="current-password" value={values.password} onChange={handleChange} required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon sx={{ color: 'text.secondary' }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setValues({ ...values, showPassword: !values.showPassword })}
                                                            edge="end"
                                                            size="large"
                                                        >
                                                            {values.showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <Button
                                            fullWidth type="submit" variant="contained" size="large"
                                            disabled={loading}
                                            sx={{
                                                py: 1.5,
                                                background: 'linear-gradient(90deg, #43a047 30%, #66bb6a 90%)',
                                                transition: 'transform 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-3px)',
                                                }
                                            }}
                                        >
                                            {loading ? <CircularProgress size={26} color="inherit" /> : 'Log In'}
                                        </Button>
                                    </Stack>
                                </Box>

                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 3, width: '100%' }}>
                                    <MuiLink component={RouterLink} to="/forgot-password" variant="body2" sx={{ color: 'text.secondary' }}>
                                        Forgot password?
                                    </MuiLink>
                                    <MuiLink component={RouterLink} to="/register" variant="body2" sx={{ color: 'primary.main', fontWeight: '600' }}>
                                        Don't have an account?
                                    </MuiLink>
                                </Stack>
                            </Paper>
                        </Fade>
                    </Box>
                </Container>
            </Box>

            <Footer />
        </Box>
    );
}

